<template>
  <v-container>
    <v-card
      class="mx-auto"
      max-width="1200"
    >
      <v-card-title>Vendor Settings</v-card-title>
      <v-card-subtitle class="mb-4">
        <v-row>
          <v-col cols="12">
            <div class="description">
              <v-icon
                color="primary"
                left
                >mdi-information-outline</v-icon
              >
              <span>
                Toggle the switches below to enable or disable each vendor for every environment. <br />
                <em>Tip: "On" means the vendor is enabled for that environment.</em><br />
                <small>Note: You have to click "Save" to apply your changes.</small>
              </span>
            </div>
          </v-col>
        </v-row>
        <v-row>
          <v-col></v-col>
          <v-col cols="2">
            <v-btn
              block
              color="success"
              @click="openAddVendorDialog"
            >
              Add Vendor
            </v-btn>
          </v-col>
        </v-row>
      </v-card-subtitle>
      <v-card-text>
        <v-table class="vendor-settings-table">
          <thead>
            <tr>
              <th class="header-cell">Vendor</th>
              <th class="header-cell">Integration Type</th>
              <th
                v-for="env in envList"
                :key="env.value"
                class="header-cell"
              >
                {{ env.label }}
              </th>
              <th class="header-cell action-header">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="vendor in vendorList"
              :key="vendor.value"
              class="vendor-row"
            >
              <td class="vendor-cell">{{ vendor.label }}</td>
              <td class="text-center">
                <v-chip
                  :color="getVendorType(vendor) === 'webview' ? 'warning' : 'primary'"
                  size="small"
                  variant="tonal"
                >
                  {{ getVendorType(vendor) === 'webview' ? 'Webview' : 'API' }}
                </v-chip>
              </td>
              <td
                v-for="env in envList"
                :key="env.value"
                class="switch-cell"
              >
                <div class="switch-wrapper">
                  <v-switch
                    v-model="vendorSettings[env.value][vendor.value]"
                    :label="vendorSettings[env.value][vendor.value] ? 'Enabled' : 'Disabled'"
                    hide-details
                    inset
                    dense
                  ></v-switch>
                </div>
              </td>
              <td class="action-cell">
                <div class="d-flex">
                  <VBtn
                    icon="ri-settings-3-line"
                    color="primary"
                    size="small"
                    class="mr-1"
                    @click="openVendorSettingsDialog(vendor)"
                  >
                  </VBtn>
                  <VBtn
                    icon="ri-delete-bin-5-line"
                    color="error"
                    size="small"
                    @click="openDeleteVendorDialog(vendor)"
                  >
                  </VBtn>
                </div>
              </td>
            </tr>
          </tbody>
        </v-table>
      </v-card-text>

      <v-row justify="start">
        <v-col
          cols="2"
          class="pb-10 pt-4 pl-10"
        >
          <v-btn
            block
            color="primary"
            @click="openConfirmDialog"
          >
            Save
          </v-btn>
        </v-col>
      </v-row>
    </v-card>
    <!-- Confirmation Dialog -->
    <v-dialog
      v-model="confirmDialog"
      max-width="400"
    >
      <v-card>
        <v-card-title class="headline">Confirm Save</v-card-title>
        <v-card-text> Are you sure you want to save these vendor settings? </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="confirmDialog = false"
            >Cancel</v-btn
          >
          <v-btn
            color="primary"
            text
            @click="saveSettings"
            >Confirm</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add Vendor Dialog -->
    <v-dialog
      v-model="addVendorDialog"
      max-width="500"
      persistent
    >
      <v-card>
        <v-card-title class="headline">Add New Vendor</v-card-title>
        <v-card-text>
          <v-form ref="vendorForm">
            <v-text-field
              v-model="newVendor.name"
              label="Vendor Name *"
              :rules="[v => !!v || 'Vendor name is required']"
              required
              outlined
              dense
              class="mb-3"
            ></v-text-field>
            <v-text-field
              v-model="newVendor.aliasName"
              label="Alias Name *"
              :rules="[v => !!v || 'Alias name is required']"
              required
              outlined
              dense
              class="mb-3"
            ></v-text-field>
            <v-select
              v-model="newVendor.integrationType"
              :items="vendorTypeOptions"
              item-title="label"
              item-value="value"
              label="Integration Type *"
              :rules="[v => !!v || 'Integration type is required']"
              required
              outlined
              dense
            ></v-select>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="closeAddVendorDialog"
            :disabled="isCreatingVendor"
            >Cancel</v-btn
          >
          <v-btn
            color="success"
            text
            @click="createVendor"
            :loading="isCreatingVendor"
            >Create</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Vendor Confirmation Dialog -->
    <v-dialog
      v-model="deleteVendorDialog"
      max-width="450"
    >
      <v-card>
        <v-card-title class="headline">Delete Vendor</v-card-title>
        <v-card-text>
          Are you sure you want to delete vendor <strong>"{{ vendorToDelete?.label }}"</strong>?
          <br />
          <small class="text-error">This action cannot be undone and will remove all environment associations.</small>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="closeDeleteVendorDialog"
            :disabled="isDeletingVendor"
            >Cancel</v-btn
          >
          <v-btn
            color="error"
            text
            @click="deleteVendor"
            :loading="isDeletingVendor"
            >Delete</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Vendor Settings Dialog -->
    <v-dialog
      v-model="vendorSettingsDialog"
      max-width="550"
      persistent
    >
      <v-card>
        <v-card-title class="headline">Vendor Settings</v-card-title>
        <v-card-text v-if="isLoadingVendorSettings">
          <div class="d-flex justify-center align-center py-8">
            <v-progress-circular
              indeterminate
              color="primary"
            ></v-progress-circular>
          </div>
        </v-card-text>
        <v-card-text v-else>
          <v-row class="mb-2">
            <v-col cols="12">
              <v-text-field
                v-model="currentVendorData.name"
                label="Vendor Name"
                readonly
                variant="outlined"
                density="comfortable"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row class="mb-2">
            <v-col cols="12">
              <v-text-field
                v-model="currentVendorData.aliasName"
                label="Alias Name"
                readonly
                variant="outlined"
                density="comfortable"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-divider class="my-4"></v-divider>
          <v-row>
            <v-col cols="12">
              <h4 class="mb-4">Feature Settings</h4>
            </v-col>
          </v-row>
          <v-row class="mb-2">
            <v-col cols="12">
              <v-select
                v-model="currentVendorSettings.vendorType"
                :items="vendorTypeOptions"
                item-title="label"
                item-value="value"
                label="Vendor Type"
                variant="outlined"
                density="comfortable"
                :rules="[v => !!v || 'Vendor type is required']"
              ></v-select>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-switch
                v-model="currentVendorSettings.channelEnabled"
                color="primary"
                hide-details
                inset
              >
                <template #label>
                  <span>Channel Enabled</span>
                </template>
              </v-switch>
              <p class="text-caption text-grey ml-12 mb-4">Enable or disable this vendor's channel integration</p>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-switch
                v-model="currentVendorSettings.supportDynamicPricing"
                color="primary"
                hide-details
                inset
              >
                <template #label>
                  <span>Support Dynamic Pricing</span>
                </template>
              </v-switch>
              <p class="text-caption text-grey ml-12">Enable or disable dynamic pricing support for this vendor</p>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="closeVendorSettingsDialog"
            :disabled="isSavingVendorSettings"
            >Cancel</v-btn
          >
          <v-btn
            color="primary"
            text
            @click="saveVendorSettings"
            :loading="isSavingVendorSettings"
            :disabled="isLoadingVendorSettings"
            >Save</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
  <v-snackbar
    v-model="isShowSnackBar"
    multi-line
    :globalTop="true"
    :color="alertType"
    dismissible
    class="mb-4"
    @click:close="clearAlert"
  >
    {{ alertMessage }}
  </v-snackbar>
</template>

<script setup>
import { useConfigStore } from '#imports'
import { useAlert } from '@/composables/useAlert'
import { computed, onMounted, ref } from 'vue'

const { alertMessage, alertType, isShowSnackBar, showAlert, clearAlert } = useAlert()
const configStore = useConfigStore()

// Derived lists from configStore - make them computed so they update
const envList = computed(() =>
  configStore.environments.map(env => ({
    label: env.aliasName,
    value: env.name,
  })),
)

const vendorList = computed(() =>
  configStore.vendors.map(vendor => ({
    label: vendor.aliasName,
    value: vendor.name,
    id: vendor.id,
    vendorType: vendor.vendorType || 'api',
  })),
)

function getVendorType(vendor) {
  return vendor.vendorType || 'api'
}

// Vendor Settings Dialog state
const vendorSettingsDialog = ref(false)
const isLoadingVendorSettings = ref(false)
const isSavingVendorSettings = ref(false)
const currentVendorData = ref({
  id: null,
  name: '',
  aliasName: '',
})
const currentVendorSettings = ref({
  channelEnabled: true,
  supportDynamicPricing: true,
  vendorType: 'api',
})

const vendorTypeOptions = [
  { label: 'API', value: 'api' },
  { label: 'Webview', value: 'webview' },
]

async function openVendorSettingsDialog(vendor) {
  vendorSettingsDialog.value = true
  isLoadingVendorSettings.value = true

  try {
    const response = await $fetch(`/api/setting/vendor-settings/${vendor.id}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    })

    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch vendor settings')
    }

    currentVendorData.value = response.data.vendor
    currentVendorSettings.value = {
      channelEnabled: response.data.settings?.channelEnabled ?? true,
      supportDynamicPricing: response.data.settings?.supportDynamicPricing ?? true,
      vendorType: response.data.settings?.vendorType ?? 'api',
    }
  } catch (err) {
    showAlert(err.message || 'Failed to load vendor settings', 'error')
    vendorSettingsDialog.value = false
  } finally {
    isLoadingVendorSettings.value = false
  }
}

function closeVendorSettingsDialog() {
  vendorSettingsDialog.value = false
  currentVendorData.value = { id: null, name: '', aliasName: '' }
  currentVendorSettings.value = { channelEnabled: true, supportDynamicPricing: true, vendorType: 'api' }
}

async function saveVendorSettings() {
  isSavingVendorSettings.value = true

  try {
    const response = await $fetch(`/api/setting/vendor-settings/${currentVendorData.value.id}`, {
      method: 'PUT',
      body: {
        channelEnabled: currentVendorSettings.value.channelEnabled,
        supportDynamicPricing: currentVendorSettings.value.supportDynamicPricing,
        vendorType: currentVendorSettings.value.vendorType,
      },
      headers: { 'Content-Type': 'application/json' },
    })

    if (!response.success) {
      throw new Error(response.message || 'Failed to save vendor settings')
    }

    closeVendorSettingsDialog()
    showAlert(response.message || 'Vendor settings saved successfully!', 'success')
  } catch (err) {
    showAlert(err.message || 'Failed to save settings', 'error')
  } finally {
    isSavingVendorSettings.value = false
    setTimeout(() => {
      clearAlert()
    }, 3000)
  }
}

// Initial vendor settings state: all true
const recordMap = ref({})
const vendorSettings = ref({})

// Initialize vendorSettings based on current data
function initializeVendorSettings() {
  vendorSettings.value = {}
  envList.value.forEach(env => {
    vendorSettings.value[env.value] = {}
    vendorList.value.forEach(vendor => {
      vendorSettings.value[env.value][vendor.value] = true
    })
  })
}

// Call initialization
initializeVendorSettings()

const confirmDialog = ref(false)
const addVendorDialog = ref(false)
const isCreatingVendor = ref(false)
const vendorForm = ref(null)
const newVendor = ref({
  name: '',
  aliasName: '',
  integrationType: 'api',
})

// Delete vendor state
const deleteVendorDialog = ref(false)
const isDeletingVendor = ref(false)
const vendorToDelete = ref(null)

function openConfirmDialog() {
  confirmDialog.value = true
}

function openAddVendorDialog() {
  addVendorDialog.value = true
  newVendor.value = {
    name: '',
    aliasName: '',
    integrationType: 'api',
  }
}

function closeAddVendorDialog() {
  addVendorDialog.value = false
  newVendor.value = {
    name: '',
    aliasName: '',
    integrationType: 'api',
  }
  if (vendorForm.value) {
    vendorForm.value.reset()
  }
}

function openDeleteVendorDialog(vendor) {
  vendorToDelete.value = vendor
  deleteVendorDialog.value = true
}

function closeDeleteVendorDialog() {
  deleteVendorDialog.value = false
  vendorToDelete.value = null
}

async function deleteVendor() {
  if (!vendorToDelete.value) return

  isDeletingVendor.value = true
  try {
    // Find the vendor ID from configStore
    const vendor = configStore.vendors.find(v => v.name === vendorToDelete.value.value)
    if (!vendor) {
      throw new Error('Vendor not found')
    }

    const response = await $fetch('/api/setting/vendors', {
      method: 'DELETE',
      body: {
        id: vendor.id,
      },
      headers: { 'Content-Type': 'application/json' },
    })

    if (!response.success) {
      throw new Error(response.message || 'Failed to delete vendor')
    }

    // Refresh the config store to update vendor list
    await configStore.fetchDataVendors()

    // Reinitialize vendor settings
    initializeVendorSettings()

    // Refresh the page data
    await fetchOptions()

    closeDeleteVendorDialog()
    showAlert(response.message || 'Vendor deleted successfully', 'success')
  } catch (err) {
    showAlert(err.message || 'Failed to delete vendor', 'error')
  } finally {
    isDeletingVendor.value = false
    setTimeout(() => {
      clearAlert()
    }, 3000)
  }
}

async function createVendor() {
  if (!vendorForm.value) return

  const { valid } = await vendorForm.value.validate()
  if (!valid) {
    return
  }

  isCreatingVendor.value = true
  try {
    const response = await $fetch('/api/setting/vendors', {
      method: 'POST',
      body: {
        name: newVendor.value.name,
        aliasName: newVendor.value.aliasName,
      },
      headers: { 'Content-Type': 'application/json' },
    })

    if (!response.success) {
      throw new Error(response.message || 'Failed to create vendor')
    }

    // Save vendor settings (integration type) for the newly created vendor
    if (response.data?.id) {
      await $fetch(`/api/setting/vendor-settings/${response.data.id}`, {
        method: 'PUT',
        body: {
          channelEnabled: true,
          supportDynamicPricing: true,
          vendorType: newVendor.value.integrationType,
        },
        headers: { 'Content-Type': 'application/json' },
      })
    }

    // Refresh the config store to get the new vendor
    await configStore.fetchDataVendors()

    // Reinitialize vendor settings to include the new vendor
    initializeVendorSettings()

    // Refresh the page data to get environment_vendor_sources
    await fetchOptions()

    closeAddVendorDialog()
    showAlert(response.message || 'Vendor created successfully', 'success')
  } catch (err) {
    showAlert(err.message || 'Failed to create vendor', 'error')
  } finally {
    isCreatingVendor.value = false
    setTimeout(() => {
      clearAlert()
    }, 3000)
  }
}

async function fetchOptions() {
  try {
    const response = await $fetch(`/api/setting/environment-vendor-sources`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })
    // Map environmentId and vendorSourceId to their names
    const envIdToName = {}
    configStore.environments.forEach(env => {
      envIdToName[env.id] = env.name
    })
    const vendorIdToName = {}
    configStore.vendors.forEach(vendor => {
      vendorIdToName[vendor.id] = vendor.name
    })

    // Reset all to false first (optional, for safety)
    envList.value.forEach(env => {
      vendorList.value.forEach(vendor => {
        vendorSettings.value[env.value][vendor.value] = false
      })
    })

    // Set according to API data & build recordMap
    if (response.success && Array.isArray(response.data)) {
      recordMap.value = {}
      response.data.forEach(item => {
        const envName = envIdToName[item.environmentId]
        const vendorName = vendorIdToName[item.vendorId]
        if (envName && vendorName) {
          vendorSettings.value[envName][vendorName] = item.enabled
          recordMap.value[`${envName}_${vendorName}`] = {
            id: item.id,
            environmentId: item.environmentId,
            vendorId: item.vendorId,
          }
        }
      })
    }
  } catch (err) {
    console.error('Error fetching data:', err)
  }
}

async function saveSettings() {
  confirmDialog.value = false
  const updates = []
  for (const env of envList.value) {
    for (const vendor of vendorList.value) {
      const key = `${env.value}_${vendor.value}`
      const record = recordMap.value[key]
      if (record) {
        updates.push({
          id: record.id,
          environmentId: record.environmentId,
          vendorId: record.vendorId,
          enabled: vendorSettings.value[env.value][vendor.value],
        })
      }
    }
  }
  try {
    const response = await $fetch('/api/setting/environment-vendor-sources', {
      method: 'PUT',
      body: updates,
      headers: { 'Content-Type': 'application/json' },
    })
    if (!response.success) {
      throw new Error(response.message || 'Failed to save settings')
    }

    await fetchOptions()
    showAlert(response.message || 'Settings saved successfully', 'success')
  } catch (err) {
    showAlert(err.message || 'Failed to save settings', 'error')
  } finally {
    setTimeout(() => {
      clearAlert()
    }, 3000)
  }
}

onMounted(async () => {
  // Refresh vendor data from store to ensure we have latest data
  await configStore.fetchDataVendors()
  // Reinitialize vendor settings with fresh data
  initializeVendorSettings()
  // Fetch environment vendor source mappings
  await fetchOptions()
})
</script>

<style scoped>
.vendor-row {
  border-bottom: 1px solid #ececec;
}
.vendor-cell {
  padding-left: 10px;
  font-weight: 600;

  background: #f9f9f9;
}
.switch-cell {
  text-align: center;
  padding: 10px 0;
  min-width: 200px;
}
.description {
  display: flex;
  align-items: flex-start;
  font-size: 1rem;
  color: #555;
  background: #f1f7fa;
  border-radius: 6px;
  padding: 14px 18px;
  margin-bottom: 8px;
}
.action-header {
  text-align: center;
  width: 100px;
}
.action-cell {
  text-align: center;
  width: 100px;
}
</style>
